import { useEffect, useState, useCallback } from "react";
import {
  fetchDemoRequests,
  cancelDemoRequest,
  fetchDemoSessions,
  createDemoSession,
  rescheduleDemoSession,
  updateDemoSessionStatus,
} from "./adminCourseService";
import "./adminCourses.css";

const fmtDt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      })
    : "—";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

const REQ_STATUS_BADGE = {
  PENDING:   "ac-badge-yellow",
  SCHEDULED: "ac-badge-blue",
  COMPLETED: "ac-badge-green",
  CANCELLED: "ac-badge-red",
};

const SES_STATUS_BADGE = {
  SCHEDULED:   "ac-badge-blue",
  COMPLETED:   "ac-badge-green",
  CANCELLED:   "ac-badge-red",
  RESCHEDULED: "ac-badge-gray",
};

const SLOT_LABEL = { MORNING: "Morning", AFTERNOON: "Afternoon", EVENING: "Evening" };
const TYPE_LABEL = { BATCH: "Group Batch", ONE_ON_ONE: "One-on-One" };

/* ── Schedule Session Modal ── */
const ScheduleModal = ({ courseId, selected, onDone, onClose }) => {
  const sessionType = selected[0]?.demoType || "BATCH";
  const [scheduledAt, setScheduledAt] = useState("");
  const [durationMins, setDurationMins] = useState(30);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await createDemoSession(courseId, {
        sessionType,
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMins: Number(durationMins),
        requestIds: selected.map((r) => r.id),
      });
      onDone(`Session scheduled. Zoom link sent to ${selected.length} user${selected.length !== 1 ? "s" : ""}.`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Failed to schedule session.";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "#fff", borderRadius: 10, padding: "1.5rem", width: "100%", maxWidth: 460, boxShadow: "0 8px 30px rgba(0,0,0,0.15)" }}>
        <h3 style={{ margin: "0 0 1rem", fontSize: "1.1rem" }}>Schedule Demo Session</h3>

        {error && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.5rem 0.75rem", borderRadius: 6, marginBottom: "0.75rem", fontSize: "0.85rem" }}>{error}</div>}

        <div className="ac-form-group">
          <label>Session Type</label>
          <input value={TYPE_LABEL[sessionType] || sessionType} readOnly style={{ background: "#f9fafb" }} />
        </div>

        <div className="ac-form-group">
          <label>Selected Users</label>
          <div style={{ fontSize: "0.85rem", color: "#374151", background: "#f9fafb", borderRadius: 6, padding: "0.5rem 0.75rem", border: "1px solid #e5e7eb" }}>
            {selected.map((r) => r.userName).join(", ")}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="ac-form-row">
            <div className="ac-form-group">
              <label>Date & Time *</label>
              <input type="datetime-local" required value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
            </div>
            <div className="ac-form-group">
              <label>Duration (mins)</label>
              <input type="number" min={15} max={120} value={durationMins} onChange={(e) => setDurationMins(e.target.value)} />
            </div>
          </div>
          <div className="ac-form-actions">
            <button type="submit" className="ac-btn ac-btn-success" disabled={saving}>
              {saving ? "Scheduling…" : "Schedule & Send Zoom Link"}
            </button>
            <button type="button" className="ac-btn ac-btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Reschedule Modal ── */
const RescheduleModal = ({ courseId, session, onDone, onClose }) => {
  const [scheduledAt, setScheduledAt] = useState("");
  const [durationMins, setDurationMins] = useState(session.durationMins || 30);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await rescheduleDemoSession(courseId, session.id, {
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMins: Number(durationMins),
      });
      onDone("Session rescheduled. New Zoom link sent to linked users.");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Reschedule failed.";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "#fff", borderRadius: 10, padding: "1.5rem", width: "100%", maxWidth: 420, boxShadow: "0 8px 30px rgba(0,0,0,0.15)" }}>
        <h3 style={{ margin: "0 0 0.25rem", fontSize: "1.1rem" }}>Reschedule Demo Session</h3>
        <p style={{ fontSize: "0.82rem", color: "#6b7280", margin: "0 0 1rem" }}>
          {TYPE_LABEL[session.sessionType]} · currently {fmtDt(session.scheduledAt)}
        </p>

        {error && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.5rem 0.75rem", borderRadius: 6, marginBottom: "0.75rem", fontSize: "0.85rem" }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="ac-form-row">
            <div className="ac-form-group">
              <label>New Date & Time *</label>
              <input type="datetime-local" required value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
            </div>
            <div className="ac-form-group">
              <label>Duration (mins)</label>
              <input type="number" min={15} max={120} value={durationMins} onChange={(e) => setDurationMins(e.target.value)} />
            </div>
          </div>
          <div className="ac-form-actions">
            <button type="submit" className="ac-btn ac-btn-warning" disabled={saving}>
              {saving ? "Rescheduling…" : "Reschedule"}
            </button>
            <button type="button" className="ac-btn ac-btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Requests sub-tab ── */
const RequestsTab = ({ courseId, onSchedule }) => {
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]); // ids of selected BATCH PENDING rows
  const [cancelling, setCancelling] = useState(null);
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setSelected([]);
    try {
      const params = {};
      if (typeFilter !== "ALL") params.type = typeFilter;
      if (statusFilter !== "ALL") params.status = statusFilter;
      const res = await fetchDemoRequests(courseId, params);
      setRequests(res.data || []);
    } catch {
      setError("Failed to load demo requests.");
    } finally {
      setLoading(false);
    }
  }, [courseId, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  const handleCancel = async (req) => {
    if (!window.confirm(`Cancel demo request for ${req.userName}?`)) return;
    setCancelling(req.id);
    setError("");
    try {
      await cancelDemoRequest(courseId, req.id);
      showToast("Request cancelled.");
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Cancel failed.";
      setError(String(msg));
    } finally {
      setCancelling(null);
    }
  };

  const toggleSelect = (req) => {
    if (req.demoType !== "BATCH" || req.status !== "PENDING") return;
    setSelected((prev) =>
      prev.includes(req.id) ? prev.filter((id) => id !== req.id) : [...prev, req.id]
    );
  };

  const selectedRequests = requests.filter((r) => selected.includes(r.id));

  if (loading) return <div className="ac-loading">Loading requests…</div>;

  return (
    <div>
      {/* toolbar */}
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ padding: "0.4rem 0.65rem", borderRadius: 6, border: "1px solid #d1d5db", fontSize: "0.85rem" }}
        >
          <option value="ALL">All Types</option>
          <option value="BATCH">Group Batch</option>
          <option value="ONE_ON_ONE">One-on-One</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: "0.4rem 0.65rem", borderRadius: 6, border: "1px solid #d1d5db", fontSize: "0.85rem" }}
        >
          <option value="ALL">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="SCHEDULED">Scheduled</option>
          <option value="COMPLETED">Completed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        {selected.length > 0 && (
          <button
            className="ac-btn ac-btn-success"
            onClick={() => onSchedule(selectedRequests)}
          >
            Schedule Selected ({selected.length})
          </button>
        )}
      </div>

      {toast && (
        <div style={{ background: "#dcfce7", color: "#15803d", padding: "0.5rem 0.9rem", borderRadius: 6, marginBottom: "0.75rem", fontSize: "0.85rem" }}>
          {toast}
        </div>
      )}

      {error && (
        <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.5rem 0.9rem", borderRadius: 6, marginBottom: "0.75rem", fontSize: "0.85rem" }}>
          {error}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="ac-empty">No demo requests found.</div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th style={{ width: 32 }} />
                <th>User</th>
                <th>Email</th>
                <th>Type</th>
                <th>Preferred Slot</th>
                <th>Message</th>
                <th>Status</th>
                <th>Requested At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => {
                const isSelectable = r.demoType === "BATCH" && r.status === "PENDING";
                const isSelected = selected.includes(r.id);
                return (
                  <tr key={r.id} style={{ background: isSelected ? "#eff6ff" : undefined }}>
                    <td>
                      {isSelectable && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(r)}
                        />
                      )}
                    </td>
                    <td style={{ fontWeight: 500 }}>{r.userName}</td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7280" }}>{r.userEmail}</td>
                    <td>
                      <span className={`ac-badge ${r.demoType === "BATCH" ? "ac-badge-blue" : "ac-badge-purple"}`}>
                        {TYPE_LABEL[r.demoType] || r.demoType}
                      </span>
                    </td>
                    <td>{SLOT_LABEL[r.preferredSlot] || "—"}</td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7280", maxWidth: 180 }}>
                      {r.message ? (
                        <span title={r.message}>
                          {r.message.length > 60 ? r.message.slice(0, 60) + "…" : r.message}
                        </span>
                      ) : "—"}
                    </td>
                    <td>
                      <span className={`ac-badge ${REQ_STATUS_BADGE[r.status] || "ac-badge-gray"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7280", whiteSpace: "nowrap" }}>
                      {fmtDate(r.requestedAt)}
                    </td>
                    <td>
                      <div className="ac-actions">
                        {r.status === "PENDING" && (
                          <>
                            {r.demoType === "ONE_ON_ONE" && (
                              <button
                                className="ac-btn ac-btn-success ac-btn-sm"
                                onClick={() => onSchedule([r])}
                              >
                                Schedule
                              </button>
                            )}
                            <button
                              className="ac-btn ac-btn-danger ac-btn-sm"
                              disabled={cancelling === r.id}
                              onClick={() => handleCancel(r)}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ── Sessions sub-tab ── */
const SessionsTab = ({ courseId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [actioning, setActioning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchDemoSessions(courseId);
      setSessions(res.data || []);
    } catch {
      setError("Failed to load demo sessions.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const handleStatus = async (session, to) => {
    const label = to === "COMPLETED" ? "mark as completed" : "cancel";
    if (!window.confirm(`Are you sure you want to ${label} this session?`)) return;
    setActioning(session.id);
    setError("");
    try {
      await updateDemoSessionStatus(courseId, session.id, to);
      showToast(to === "COMPLETED" ? "Session marked as completed." : "Session cancelled.");
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Action failed.";
      setError(String(msg));
    } finally {
      setActioning(null);
    }
  };

  if (loading) return <div className="ac-loading">Loading sessions…</div>;

  return (
    <div>
      {toast && (
        <div style={{ background: "#dcfce7", color: "#15803d", padding: "0.5rem 0.9rem", borderRadius: 6, marginBottom: "0.75rem", fontSize: "0.85rem" }}>
          {toast}
        </div>
      )}
      {error && (
        <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.5rem 0.9rem", borderRadius: 6, marginBottom: "0.75rem", fontSize: "0.85rem" }}>
          {error}
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="ac-empty">No demo sessions scheduled yet.</div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Scheduled At</th>
                <th>Duration</th>
                <th>Zoom Link</th>
                <th>Status</th>
                <th>Linked Users</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const linked = s.linkedRequests || [];
                const names = linked.slice(0, 2).map((r) => r.userName).join(", ");
                const extra = linked.length > 2 ? ` (+${linked.length - 2})` : "";
                return (
                  <tr key={s.id}>
                    <td>
                      <span className={`ac-badge ${s.sessionType === "BATCH" ? "ac-badge-blue" : "ac-badge-purple"}`}>
                        {TYPE_LABEL[s.sessionType] || s.sessionType}
                      </span>
                    </td>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.85rem" }}>{fmtDt(s.scheduledAt)}</td>
                    <td>{s.durationMins} min</td>
                    <td>
                      {(s.zoomStartUrl || s.zoomJoinUrl) ? (
                        <a
                          href={s.zoomStartUrl || s.zoomJoinUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="ac-btn ac-btn-primary ac-btn-sm"
                        >
                          Join
                        </a>
                      ) : "—"}
                    </td>
                    <td>
                      <span className={`ac-badge ${SES_STATUS_BADGE[s.status] || "ac-badge-gray"}`}>
                        {s.status}
                      </span>
                    </td>
                    <td style={{ fontSize: "0.85rem" }}>
                      {names}{extra || (linked.length === 0 ? "—" : "")}
                    </td>
                    <td>
                      <div className="ac-actions">
                        {s.status === "SCHEDULED" && (
                          <>
                            <button
                              className="ac-btn ac-btn-warning ac-btn-sm"
                              disabled={actioning === s.id}
                              onClick={() => setRescheduleTarget(s)}
                            >
                              Reschedule
                            </button>
                            <button
                              className="ac-btn ac-btn-success ac-btn-sm"
                              disabled={actioning === s.id}
                              onClick={() => handleStatus(s, "COMPLETED")}
                            >
                              Complete
                            </button>
                            <button
                              className="ac-btn ac-btn-danger ac-btn-sm"
                              disabled={actioning === s.id}
                              onClick={() => handleStatus(s, "CANCELLED")}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rescheduleTarget && (
        <RescheduleModal
          courseId={courseId}
          session={rescheduleTarget}
          onDone={(msg) => { setRescheduleTarget(null); showToast(msg); load(); }}
          onClose={() => setRescheduleTarget(null)}
        />
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main tab component
───────────────────────────────────────────── */
const AdminDemoTab = ({ courseId }) => {
  const [subTab, setSubTab] = useState("requests");
  const [scheduleFor, setScheduleFor] = useState(null); // array of request objects
  const [toast, setToast] = useState("");
  const [reloadSessions, setReloadSessions] = useState(0);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const handleScheduleDone = (msg) => {
    setScheduleFor(null);
    showToast(msg);
    setReloadSessions((n) => n + 1);
  };

  const subTabStyle = (name) => ({
    padding: "0.4rem 1rem",
    border: "none",
    background: subTab === name ? "#2563eb" : "#e5e7eb",
    color: subTab === name ? "#fff" : "#374151",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.85rem",
  });

  return (
    <div>
      {/* sub-tab switcher */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem" }}>
        <button style={subTabStyle("requests")} onClick={() => setSubTab("requests")}>
          Demo Requests
        </button>
        <button style={subTabStyle("sessions")} onClick={() => setSubTab("sessions")}>
          Demo Sessions
        </button>
      </div>

      {toast && (
        <div style={{ background: "#dcfce7", color: "#15803d", padding: "0.5rem 0.9rem", borderRadius: 6, marginBottom: "0.75rem", fontSize: "0.85rem" }}>
          {toast}
        </div>
      )}

      {subTab === "requests" && (
        <RequestsTab
          courseId={courseId}
          onSchedule={(reqs) => setScheduleFor(reqs)}
        />
      )}

      {subTab === "sessions" && (
        <SessionsTab key={reloadSessions} courseId={courseId} />
      )}

      {scheduleFor && (
        <ScheduleModal
          courseId={courseId}
          selected={scheduleFor}
          onDone={handleScheduleDone}
          onClose={() => setScheduleFor(null)}
        />
      )}
    </div>
  );
};

export default AdminDemoTab;
